/**
 * The gold a favourited word carries, wherever one is shown — the vocab table and
 * the flashcard overlay each had their own near-miss of this colour.
 */
export const FAVOURITE_GOLD = '#E8A317';

/**
 * The one content width for the homepage.
 *
 * Sections each used to centre themselves at their own max width (1200, 1180,
 * 1100, 1060, 1000, 900, 800), so every heading started at a different x — a
 * 200px spread down the page, with nothing sharing a left edge. One value means
 * every heading, rule and card column lines up on a single vertical.
 *
 * Body copy still gets a narrower `maxWidth` for line length, but left-aligned
 * inside this container rather than re-centred, which is what broke the edge.
 */
export const CONTENT_MAX_WIDTH = 1180;

/** Comfortable measure for a paragraph — roughly 70 characters. */
export const TEXT_MAX_WIDTH = 720;

/**
 * `scroll-margin-top` for anything an in-page anchor targets, so the heading does not
 * land under the navbar. `--navbar-h-max` is published by Navbar (see its HideOnScroll)
 * and is the bar's height even while it is hidden on mobile — scrolling up brings the
 * bar straight back over whatever we just scrolled to. The extra 40px is breathing room.
 */
export const ANCHOR_SCROLL_MARGIN = 'calc(var(--navbar-h-max) + 40px)';

/**
 * The red from the logo (`public/logo.png`), sampled exactly.
 *
 * The mark carries a pure red that appears nowhere else in the palette, which is
 * why it reads as foreign against the navy theme. A colour used once looks like a
 * mistake; repeated in a consistent role it reads as intentional — and red on
 * white is, conveniently, the most Swiss pairing there is.
 *
 * DECORATIVE RULES ONLY. Never use it for text, icons or anything conveying
 * state: `theme.palette.error.main` (#DC2626) is the error signal, and a second
 * red carrying meaning would blur that. Keep this to thin accent rules.
 */
export const ACCENT_RED = '#FF0000';

/** The standard accent rule: short, rounded, sits under a heading. */
export const accentRuleSx = {
  width: 44,
  height: 3,
  borderRadius: 99,
  bgcolor: ACCENT_RED,
  flex: '0 0 auto',
};

/**
 * The one soft surface: `secondary.main` at 8%.
 *
 * Anything that needs to sit back — icon tiles, chapter chips, the level pills,
 * a sample-card header — uses this. It was already in the exam section as the
 * selected toggle's fill; everywhere else had drifted to a neutral grey
 * (`#F4F6FA`) or a slate tint, so soft surfaces came in three near-identical
 * shades from two different hue families. One value keeps quiet areas quiet and
 * stops any single section reading as more emphasised than its neighbours.
 *
 * Resolves to roughly #F2F6FA over white — light enough that the pastel glyph
 * colour still clears contrast, which is why mapTokens warns against putting
 * pastel icons on the theme's heavier `background.paper` blue.
 */
export const SOFT_BLUE = 'rgba(96, 139, 193, 0.08)';

/**
 * The two page bands.
 *
 * Sections alternate between these, and the alternation is assigned in Home.jsx
 * rather than chosen by each section — when every component picked its own tone
 * the page ended up with three tinted sections in a row and nothing read as a
 * division.
 *
 * The tint is deliberately subtle: it is the app's existing `whitesmoke`, and it
 * separates sections by rhythm rather than by contrast. That works now only
 * because the alternation is strict — the same tone repeated three times in a row
 * is what made it unreadable before, not the tone itself.
 */
export const BAND_PLAIN = '#FFFFFF';
export const BAND_TINT = 'whitesmoke';

/**
 * The brand navy, and the deeper shade it moves to on hover.
 *
 * Single source: the cap, the card button and the homepage content module each
 * had their own copy of this hex, which is how the original nine-identical-buttons
 * problem started. Anything navy on the homepage derives from here.
 */
export const BRAND_NAVY = '#133E87';
export const BRAND_NAVY_DEEP = '#0F3068';

/**
 * The coloured cap along the top edge of a homepage card.
 *
 * Navy, not the structural red. Extending red from the heading rules to five card
 * caps plus a navbar border was tried and was too much at once: a thin rule under
 * a heading is a mark, but five caps read as a colour scheme. Red stays rationed
 * to the heading rules and the nav indicator.
 *
 * One colour for every card rather than one per card: these are equal options,
 * and a palette of caps implied a categorisation that does not exist.
 */
export const CARD_CAP = BRAND_NAVY;

export const GRADIENT = 'linear-gradient(135deg, #608BC1 0%, #133E87 100%)';
export const GRADIENT_HOVER = 'linear-gradient(135deg, #133E87 0%, #0F3068 100%)';
export const SHADOW_CTA = '0 8px 16px -4px rgba(19,62,135,0.28)';
export const SHADOW_CTA_HOVER = '0 12px 22px -4px rgba(19,62,135,0.4)';

/**
 * Three CTA tiers, in descending weight.
 *
 * The homepage used to give every button the same gradient pill — nine of them
 * across five sections — so nothing read as the primary action and the two that
 * mattered (Practice, Mock exam) were invisible among the rest. Weight is the
 * only thing that makes a call to action findable, so it is rationed here:
 * `ctaPrimarySx` is allowed at most twice on a page, everything else steps down.
 *
 * `ExamSection`, `ReviewSection`, `PracticeSection` and `ContactSection` each
 * carried their own near-identical copy of the primary style (same gradient,
 * same shadow, differing only in `width: 300` vs `320`) — those are gone.
 */

const ctaBaseSx = {
  fontWeight: 700,
  fontSize: { xs: '1rem', sm: '1.1rem' },
  textTransform: 'none',
  borderRadius: '999px',
  px: { xs: 3, sm: 5 },
  py: { xs: 1.2, sm: 1.5 },
  whiteSpace: 'nowrap',
};

/**
 * The one loud button. The pulse draws the eye on first paint, but an animation
 * that never stops is both distracting and a vestibular-accessibility problem,
 * so it is disabled outright when the visitor has asked for reduced motion.
 */
export const ctaPrimarySx = {
  ...ctaBaseSx,
  width: { xs: '100%', sm: 'auto' },
  minWidth: { sm: 260 },
  color: '#fff',
  background: GRADIENT,
  boxShadow: SHADOW_CTA,
  animation: 'cta-pop 3.6s ease-in-out infinite',
  '@keyframes cta-pop': {
    '0%, 70%, 100%': { transform: 'scale(1)', boxShadow: SHADOW_CTA },
    '15%': { transform: 'scale(1.04)', boxShadow: SHADOW_CTA_HOVER },
    '30%': { transform: 'scale(1)', boxShadow: SHADOW_CTA },
  },
  '&:hover': { background: GRADIENT_HOVER, animation: 'none' },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

/** The considered second choice: same size, no fill, no motion. */
export const ctaSecondarySx = {
  ...ctaBaseSx,
  width: { xs: '100%', sm: 'auto' },
  minWidth: { sm: 260 },
  background: 'transparent',
  boxShadow: 'none',
  color: 'primary.main',
  border: '1.5px solid',
  borderColor: 'primary.main',
  '&:hover': {
    background: 'rgba(19,62,135,0.06)',
    borderColor: 'primary.main',
    boxShadow: 'none',
  },
};

/**
 * The action inside a card, for a set of parallel choices.
 *
 * Solid brand fill rather than the gradient: several of these sit side by side as
 * equal options, and repeating the primary treatment would undo the hierarchy the
 * tiers exist to create. They still read unmistakably as buttons.
 *
 * The arrow slides out and the fill deepens on hover, so the whole card feels
 * like one expanding target rather than a static panel with a link on it.
 */
export const ctaCardSx = {
  ...ctaBaseSx,
  width: '100%',
  py: 1.1,
  fontSize: '0.9375rem',
  color: '#fff',
  bgcolor: BRAND_NAVY,
  boxShadow: 'none',
  transition: 'background-color .2s ease, box-shadow .2s ease',
  '& .MuiButton-endIcon': { transition: 'transform .2s ease' },
  '&:hover': {
    bgcolor: BRAND_NAVY_DEEP,
    boxShadow: SHADOW_CTA,
    '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '& .MuiButton-endIcon': { transition: 'none' },
    '&:hover .MuiButton-endIcon': { transform: 'none' },
  },
};

/**
 * The gradient button used for entering the product from inside a card.
 *
 * Carries the brand gradient like `ctaPrimarySx`, but expands on hover rather
 * than pulsing on a loop. The hero and the mock-exam CTA are three screens apart
 * from these and never share a viewport, so the gradient does not compete — but
 * four buttons pulsing continuously is exactly the restlessness the tier system
 * was introduced to remove. Motion on intent, not on arrival.
 */
export const ctaGradientSx = {
  ...ctaBaseSx,
  width: 'auto',
  px: 3,
  py: 1.15,
  fontSize: '0.9375rem',
  color: '#fff',
  background: GRADIENT,
  boxShadow: SHADOW_CTA,
  transition: 'transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease, background .22s ease',
  '& .MuiButton-endIcon': { transition: 'transform .22s cubic-bezier(.2,.8,.2,1)' },
  '&:hover': {
    background: GRADIENT_HOVER,
    transform: 'scale(1.04)',
    boxShadow: SHADOW_CTA_HOVER,
    '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
  },
  '&:active': { transform: 'scale(1.01)' },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '& .MuiButton-endIcon': { transition: 'none' },
    '&:hover': { transform: 'none', '& .MuiButton-endIcon': { transform: 'none' } },
  },
};

/** In-section navigation. Reads as a link, not a button. */
export const ctaTertiarySx = {
  ...ctaBaseSx,
  px: 1,
  py: 0.5,
  fontSize: '1rem',
  width: 'auto',
  minWidth: 0,
  color: 'primary.main',
  background: 'transparent',
  boxShadow: 'none',
  '& .MuiButton-endIcon': { transition: 'transform .2s ease' },
  '&:hover': {
    background: 'transparent',
    textDecoration: 'underline',
    '& .MuiButton-endIcon': { transform: 'translateX(3px)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .MuiButton-endIcon': { transition: 'none' },
    '&:hover .MuiButton-endIcon': { transform: 'none' },
  },
};

/**
 * The pre-tier CTA style, kept for the pages that already spread it (exam
 * overview, pricing, exam tips, 404, the mock-exam sample preview). Those call
 * sites rely on the old fixed `sm: 320` width rather than overriding it, so it is
 * pinned here to keep them unchanged. New code should name the tier it wants.
 */
export const ctaSx = { ...ctaPrimarySx, width: { xs: '100%', sm: 320 }, minWidth: 0 };
